/**
 * Readable ticket number for display — mirrors the `simple_ticket_number ??
 * ticket_number` pattern used across Ticket Details / Dayboard / Command Center
 * (e.g. TicketDetailsPopup, TicketCard, OptiboardTicketRow). NEVER returns a raw
 * UUID: if there is no readable number, returns null so callers can fall back to a
 * neutral label instead of leaking the id.
 */
export interface TicketNumberFields {
  /** NOV3 A6: the repair's number (server computed field `repair_number`) — the chain head's for every rollover day. */
  repair_number?: number | null;
  simple_ticket_number?: number | null;
  ticket_number?: string | null;
}

/**
 * NOV3 A6 (founder requirement 2026-11-03): one repair = one customer-visible number. Every day of a multi-day repair
 * shows the chain head's number (`repair_number`); a row without it falls back to its own number.
 */
export function ticketDisplayNumber(ticket: TicketNumberFields | null | undefined): string | null {
  if (!ticket) return null;
  const num = ticket.repair_number ?? ticket.simple_ticket_number ?? ticket.ticket_number;
  if (num == null) return null;
  const s = String(num).trim();
  return s === '' ? null : s;
}

/** "#TKT-0152" — the repair's number padded to four digits; the id's first 8 characters only when no number exists. */
export function formatTicketNumber(ticket: (TicketNumberFields & { id?: string | null }) | null | undefined): string {
  const n = ticketDisplayNumber(ticket);
  if (n != null) return `#TKT-${/^\d+$/.test(n) ? n.padStart(4, '0') : n}`;
  return ticket?.id ? `#${String(ticket.id).slice(0, 8)}` : '#—';
}
